import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { store } from '../../../redux/store';
import { Unsubscribe } from '@stencil/redux';
import {
  addItemToCart,
  canRemoveItemFromCart,
  changeBookingMileage,
  changeBookingVariation,
  changeMileageOption,
  fetchItemsForCurrentStep,
  fetchPrice,
  initForOrder,
  openCart,
  openDetails,
  openReservation,
  removeItemFromCart,
} from '../../../redux/actions';
import { ApiError } from '../../../api/client';
import { BookingParameters, SearchParameters, ComponentStatus } from '../../rentware-search/utils';
import { ComponentView, ShortArticle } from '../../../types/types';
import { dateToHourString, dateToString } from '../../../utils/utils';
import { BookingDetails, VariationItem } from '../../../redux/types';
import { t } from '../../../redux/utils';
import { mileageIdInBookingDetails, variationIdInBookingDetails } from '../../rtr-article-details/utils/utils';
import { RootState } from '../../../redux/reducers';

@Component({
  tag: 'rtr-short-article',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RtrShortArticle {
  @Prop() article: ShortArticle;
  @Prop() view: ComponentView = ComponentView.List;
  @Prop() parent: ComponentView;
  @Prop() waitingForUpdate: boolean = false;
  @Prop() componentStatus: ComponentStatus = ComponentStatus.Ready;
  @Prop() bookingDetails: BookingDetails;
  @Prop() searchParameters: SearchParameters;
  @Prop() onArticlesLoaded: () => void;

  @Prop({ mutable: true }) bookingParams: BookingParameters = null;
  @Prop({ mutable: true }) reservationPopup: ComponentView[] = [];

  @State() amountToBook: number = 1;
  @State() isWaiting: boolean = false;

  @Event() clickForOpenLocationPicker: EventEmitter;
  @Event() clickForOpenDatePicker: EventEmitter;

  unsubscribe!: Unsubscribe;
  openDetails!: typeof openDetails;
  openCart!: typeof openCart;
  openReservation!: typeof openReservation;
  initForOrder!: typeof initForOrder;
  addItemToCart!: typeof addItemToCart;
  removeItemFromCart!: typeof removeItemFromCart;
  fetchItemsForStep!: typeof fetchItemsForCurrentStep;
  fetchPrice!: typeof fetchPrice;
  canRemoveItemFromCart!: typeof canRemoveItemFromCart;
  changeMileageOption!: typeof changeMileageOption;
  changeBookingVariation!: typeof changeBookingVariation;
  changeBookingMileage!: typeof changeBookingMileage;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      if (!this.bookingParams && !!state.orderDetails) {
        this.bookingParams = {
          fromDate: state.orderDetails.fromDate,
          toDate: state.orderDetails.toDate,
          people: state.orderDetails.people,
        };
      }

      return {
        reservationPopup: state.system.widgetConfig.advanced.general.reservationPopup,
      };
    });

    store.mapDispatchToProps(this, {
      openCart,
      openDetails,
      openReservation,
      fetchItemsForStep: fetchItemsForCurrentStep,
      initForOrder,
      addItemToCart,
      removeItemFromCart,
      fetchPrice,
      canRemoveItemFromCart,
      changeMileageOption,
      changeBookingVariation,
      changeBookingMileage,
    });

    if (this.article.mileageOptions && !this.selectedMileageId) {
      this.changeBookingMileage({ [this.article.id]: this.article.mileageOptions[0].id });
    }

    if (this.article.variation && !this.selectedVariationId) {
      const variationItems = this.article.variation.items;
      this.changeBookingVariation({ [this.article.id]: variationItems[0].id });
    }
  }

  componentWillRender() {
    if (this.amountToBook < this.article?.minQuantity) this.amountToBook = this.article.minQuantity;
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  get selectedMileageId() {
    return mileageIdInBookingDetails(this.article.id, this.bookingDetails);
  }

  get selectedVariationId() {
    return variationIdInBookingDetails(this.article.id, this.bookingDetails);
  }

  setArticleWaiting = () => {
    this.isWaiting = true;
  };

  setArticleActive = () => {
    this.isWaiting = false;
  };

  handleMileageChange = (optionId: string) => {
    if (!this.article.orderItemId) {
      this.changeBookingMileage({ [this.article.id]: optionId });
    } else {
      this.setArticleWaiting();
      this.changeMileageOption(
        this.article.id,
        optionId,
        () => {
          this.initForOrder(this.setArticleActive);
        },
        this.setArticleActive,
      );
    }
  };

  handleVariationChange = (id: string) => {
    this.amountToBook = 1;
    this.changeBookingVariation({ [this.article.id]: id });
  };

  addToCart = () => {
    this.setArticleWaiting();

    let variationsList = [];
    if (this.selectedVariationId) {
      variationsList = Array(this.amountToBook).fill(this.selectedVariationId);
    }

    this.addItemToCart(
      {
        itemId: this.article.id,
        amountToAdd: this.amountToBook,
        dateRange: !!this.bookingParams
          ? {
              from: this.bookingParams.fromDate,
              to: this.bookingParams.toDate,
            }
          : null,
        timeSlot: this.bookingParams.timeSlot,
        people: this.bookingParams?.people,
        variationsIds: variationsList,
        mileageOptionId: this.selectedMileageId,
        price: this.article.price,
        name: this.article.name,
      },
      () => {
        this.fetchItemsForStep(() => {
          const showReservation = this.reservationPopup?.includes(this.parent);

          this.setArticleActive();
          if (showReservation) {
            this.openReservation(this.article.id);
          }
        });
      },
      err => {
        this.setArticleActive();
        if (err instanceof ApiError) {
          /*let msg = 'Error!';
                    if (err.data && err.data.reason && err.data.reason.domain === 'step' && err.data.reason.id === 'max') {
                        msg = trans('checkout.validationStepMaxQuantityError', this.locale, this.translations, { quantity: err.data.reason.context.max });
                    }
                    //alert(msg);*/
        }
      },
    );
  };

  handleChangeAmount = (newAmount, oldAmount) => {
    if (newAmount >= 0 && newAmount < 1000) {
      this.setArticleWaiting();

      const difference = newAmount - oldAmount;
      let variationsList = [];
      if (this.selectedVariationId) {
        variationsList = Array(Math.abs(difference)).fill(this.selectedVariationId);
      }

      if (difference >= 1) {
        this.addItemToCart(
          {
            itemId: this.article.id,
            amountToAdd: difference,
            variationsIds: variationsList,
            people: this.bookingParams?.people,
            name: this.article.name,
          },
          () => {
            this.fetchItemsForStep(this.setArticleActive);
          },
          this.setArticleActive,
        );
      } else if (difference <= 0) {
        this.canRemoveItem(
          this.article.id,
          () => {
            this.removeItemFromCart(
              {
                itemId: this.article.orderItemId,
                amountToRemove: -difference,
                variationsIds: variationsList,
              },
              () => {
                this.fetchItemsForStep(this.setArticleActive);
              },
              this.setArticleActive,
            );
          },
          this.setArticleActive,
        );
      }
    }
  };

  handleRemove = () => {
    this.setArticleWaiting();

    if (this.selectedVariationId) {
      const variationQuantity = this.selectedVariation.quantityInCart;
      const variationsList = Array(variationQuantity).fill(this.selectedVariationId);

      this.canRemoveItem(
        this.article.id,
        () => {
          this.removeItemFromCart(
            {
              itemId: this.article.orderItemId,
              amountToRemove: variationQuantity,
              variationsIds: variationsList,
            },
            () => {
              this.fetchItemsForStep(this.setArticleActive);
            },
            this.setArticleActive,
          );
        },
        this.setArticleActive,
      );
    } else {
      this.removeItemFromCart(
        { itemId: this.article.orderItemId, amountToRemove: 0 },
        () => {
          this.fetchItemsForStep(this.setArticleActive);
        },
        this.setArticleActive,
      );
    }
  };

  canRemoveItem(itemId: string, onYes = () => null, onNo = () => null) {
    this.canRemoveItemFromCart(
      itemId,
      data => {
        if (!data.success) {
          if (confirm(t('checkout.stepRulesClearCartWarning'))) {
            onYes();
          } else {
            onNo();
          }
        } else {
          onYes();
        }
      },
      this.setArticleActive,
    );
  }

  handleChangeAmountToBook = newAmount => {
    this.amountToBook = newAmount;
    this.fetchPriceWithoutWaiting();
  };

  handleButtonClick = () => {
    this.addToCart();
  };

  handleNotAvailableButtonClick = () => {
    if (this.componentStatus === ComponentStatus.PickLocation) {
      this.clickForOpenLocationPicker.emit();
    } else if (this.componentStatus === ComponentStatus.PickDate) {
      this.clickForOpenDatePicker.emit();
    }
  };

  fetchPriceWithoutWaiting = () => {
    const fromDate = new Date(this.bookingParams.fromDate);
    const toDate = new Date(this.bookingParams.toDate);

    const fromTime = dateToHourString(fromDate);
    const toTime = dateToHourString(toDate);

    this.fetchPrice(
      this.article.id,
      this.selectedVariationId,
      {
        from: dateToString(fromDate),
        to: dateToString(toDate),
      },
      {
        from: fromTime,
        to: toTime,
      },
      this.bookingParams.timeSlot,
      this.amountToBook,
      (data: any) => {
        this.article = {
          ...this.article,
          available: data.available,
          price: {
            ...this.article.price,
            gross: data.price,
            net: data.netPrice,
          },
        };

        this.setArticleActive();
      },
      this.setArticleActive,
    );
  };

  get calculatedQuantity() {
    if (this.article.quantityInCart > 0) {
      if (this.article.variation) {
        if (this.article.variation && this.selectedVariation?.quantityInCart > 0) {
          return this.selectedVariation?.quantityInCart;
        }

        return this.amountToBook;
      }

      return this.article.quantityInCart;
    }

    if (!this.article.available) {
      return 0;
    }

    return this.amountToBook;
  }

  get calculatedMaxQuantity() {
    if (this.selectedVariationId) {
      return this.selectedVariation?.maxQuantity;
    }

    return this.article.maxQuantity;
  }

  get selectedVariation(): VariationItem {
    return this.article.variation?.items?.find(varItem => varItem.id === this.selectedVariationId);
  }

  get articleKey(): string {
    return this.article?.id;
  }

  get hasSelectedVariationInCart(): boolean {
    return !!this.article.variation?.items.find(varItem => varItem.id === this.selectedVariationId && varItem.quantityInCart > 0);
  }

  get articleWithState(): ShortArticle {
    return {
      ...this.article,
      isWaiting: this.isWaiting || this.waitingForUpdate,
      calculatedQuantity: this.calculatedQuantity,
      calculatedMaxQuantity: this.calculatedMaxQuantity,
      selectedVariationId: this.selectedVariationId,
      selectedMileageId: this.selectedMileageId,
      hasSelectedVariationInCart: this.hasSelectedVariationInCart,
    };
  }

  get cardStatusInfo(): string | Node {
    if (this.article.hasSelectedVariationInCart || (!this.article.variation && this.article.quantityInCart > 0)) {
      return <rentware-expiration-timer />;
    }

    if (this.componentStatus !== ComponentStatus.Ready) {
      return t(`search.${this.componentStatus}`);
    }

    if (!this.article.available && !this.article.hasAvailableVariation) {
      return t('search.notAvailable');
    }

    const mileagePrice = this.article?.mileageOptions?.find(item => item.id === this.article.selectedMileageId)?.price;

    return (
      <Fragment>
        {t('articleBooking.addToCart')}
        <rentware-item-price price={this.article.price} mileagePrice={mileagePrice} />
      </Fragment>
    );
  }

  get cardStatusAction() {
    if (this.article.hasSelectedVariationInCart || (!this.article.variation && this.article.quantityInCart > 0)) {
      return this.openCart;
    }

    if (this.componentStatus !== ComponentStatus.Ready) {
      return this.handleNotAvailableButtonClick;
    }

    if (!this.article.available) return;

    return () => this.openDetails(this.article.id, true);
  }

  render() {
    if (this.view === ComponentView.Cards) {
      return (
        <rtr-short-article-card
          key={this.articleKey}
          article={this.articleWithState}
          statusInfo={this.cardStatusInfo}
          onStatusInfoAction={this.cardStatusAction}
          onClickForDetails={this.openDetails}
          onArticlesLoaded={this.onArticlesLoaded}
        />
      );
    }

    return (
      <rtr-short-article-list
        key={this.articleKey}
        article={this.articleWithState}
        onVariationChange={this.handleVariationChange}
        onMileageChange={this.handleMileageChange}
        componentStatus={this.componentStatus}
        onButtonClick={this.handleButtonClick}
        onChangeAmountToBook={this.handleChangeAmountToBook}
        onChangeAmount={this.handleChangeAmount}
        onRemoveArticle={this.handleRemove}
        onClickForDetails={this.openDetails}
        onArticlesLoaded={this.onArticlesLoaded}
        onNotAvailableButtonClick={this.handleNotAvailableButtonClick}
      />
    );
  }
}
